import * as React from 'react';

import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import { SidePanelSheet } from '@gitbook/react-uikit';
import { assertIsDefined } from '@gitbook/util-ts';

import { useCurrentHivePage } from '../../../../Components';
import { useNavigation, useSetAnalyticsScreen } from '../../../../navigation';
import { useAdaptiveContextForSpace } from '../../../Site';
import { PageOptionsTabLayout } from './PageOptionsTabLayout';
import { PageOptionsTabVisibility } from './PageOptionsTabVisibility';
import type { PageOptionsSidePanelProps } from './types';

/**
 * Side panel to configure page options (layout, images, content, sidebars, footer)
 */
export function PageOptionsSidePanel(props: PageOptionsSidePanelProps): React.ReactElement {
    const { screen, space, analyticsContext } = props;

    const { navigate } = useNavigation();

    const branch = useHiveBranch();
    assertIsDefined(branch);
    const selectedTab = screen.sideSheet.tab ?? 'layout';

    const { revision } = useHiveRevision();

    const [page] = useCurrentHivePage(revision, screen.page);

    const adaptiveContentContext = useAdaptiveContextForSpace(space);

    const onClose = React.useCallback(
        (replace = false) => {
            navigate(
                {
                    current: {
                        sideSheet: undefined,
                    },
                },
                { replace }
            );
        },
        [navigate]
    );

    // If the page is not found, we close the side panel
    const pageIsMissing = !page;
    React.useEffect(() => {
        if (pageIsMissing) {
            onClose(true);
        }
    }, [pageIsMissing, onClose]);

    useSetAnalyticsScreen(
        page
            ? {
                  ...analyticsContext,
                  screen: 'space_page_options',
                  screenTab: selectedTab,
                  page: page.id,
              }
            : null,
        [analyticsContext, page?.id]
    );

    return (
        <SidePanelSheet<typeof selectedTab>
            title="Page options"
            onClose={onClose}
            selectedTab={selectedTab}
            onSelectedTabChange={(tab) => {
                navigate({
                    current: {
                        sideSheet: {
                            ...screen.sideSheet,
                            tab,
                        },
                    },
                });
            }}
            tabs={[
                {
                    key: 'layout',
                    label: 'Layout',
                },
                {
                    key: 'visibility',
                    label: 'Visibility',
                },
            ]}
        >
            {page && selectedTab === 'layout' ? (
                <PageOptionsTabLayout
                    {...props}
                    branch={branch}
                    revision={revision}
                    page={page}
                    adaptiveContentContext={adaptiveContentContext}
                />
            ) : null}
            {page && selectedTab === 'visibility' ? (
                <PageOptionsTabVisibility
                    {...props}
                    branch={branch}
                    revision={revision}
                    page={page}
                    adaptiveContentContext={adaptiveContentContext}
                />
            ) : null}
        </SidePanelSheet>
    );
}
